/**
 * OrbiDock.tsx
 * The canonical Orbi dock for app.teglo.ai. ONE orb (ChromeOrb), ONE dock, ONE
 * Vapi engine. Extended into the in-app operator agent.
 *
 * IDENTITY (item 1): on the operator/platform surface the orb is a NEUTRAL,
 * strategic "Orbi" assistant. It is NOT the NOC field-tech persona
 * (network_engineer), NOT the phone "Front Door" router, and NOT a scripted
 * customer role. A customer-facing role is adopted only when it places an
 * outbound customer call. The displayed identity is always neutral Orbi; the
 * voice persona under the hood defaults to customer_support (Sarah voice), never
 * network_engineer.
 *
 * TWO EXPLICIT MODES:
 *   - PLATFORM ASK (default): dock click opens the compact panel, primed with
 *     on-screen context. Typed free-text -> orbi-operator-ask. Named quick
 *     actions each hit ONE direct backend (ask-actions answer in-panel,
 *     navigate-actions move the user to a screen).
 *   - VOICE (opt-in): the Talk button starts a voice call and the panel becomes
 *     the big-orb hero. The only path that opens the mic.
 *
 * PERSISTENCE (item 4): panel open-state, the ask transcript, and the primed
 * subject are mirrored to sessionStorage and restored on mount, so the panel
 * survives a full-page navigation and re-opens where the operator left it. A
 * LIVE voice call cannot survive a full page reload in this MPA (the JS context
 * is destroyed); that continuity needs the SPA shell and is flagged for review.
 *
 * Window bridge: window.OrbiDock.setCustomer / setPersona / open / close.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useVapi } from './src/hooks/useVapi';
import { ChromeOrb } from './src/components/ChromeOrb';
import { VoiceOverlayPanel, type QuickAction, type AskEntry, type OrbiCardModel } from './src/VoiceOverlayPanel';
import { ConnectionState, type CustomerContext } from './src/types';
import {
  readPlatformContext,
  pageIntentAnswer,
  describeContext,
  readChips,
  readGreetingName,
  readIdentity,
  buildOperatorVoiceContext,
  isWhatOnScreenQuery,
  answerWhatOnScreen,
  urlAltitude,
  tenantUnresolved,
  type PlatformContext,
  type ChipWords,
} from './src/lib/platformContext';
import {
  askOrbi,
  askStarters,
  type StarterOffer,
  requestOrbiCall,
  detectCallIntent,
  detectShowIntent,
  searchSubscribersByName,
  fetchSubjectDetail,
  operatorAuthFailureText,
  fetchWorkStream,
  type AskCitation,
  type CustomerRef,
} from './src/lib/operatorActions';
import { composeSectionWatch } from './src/lib/preemption';
import { validateUiBlocks, type UiAction, type UiBlock } from './src/lib/uiRegistry';
import { operatorSessionId, pushTrail, trailSummary, detectNavCommand, type TrailEntry } from './src/lib/continuity';
import { workspaceNavigate, workspaceActive, commitWorkspace, setWorkspaceNavListener, workspaceWindow, type NavOutcome } from './src/lib/workspace';
import {
  situationBrief,
  capabilitiesHere,
  capabilityLines,
  applyTenantOverrides,
  readOutageEvents,
  waitForSituation,
} from './src/lib/situation';

const TEGLO_VERTICAL_ID = 'f0000001-0000-0000-0000-000000000000';

/* DROP-IN (Phase 3 binding 4): the vertical identity is CONFIG, not a
   constant. Resolution order: __ORBI_ENV__.ORBI_VERTICAL_ID, the
   __ORBI_ENV__.ORBI_SCOPE bag (the one-script-tag config surface), the host
   shell's ORA scope, and only then the Teglo constant.
   FAIL CLOSED (operator repro 2026-08-27): "only then the Teglo constant" was
   unconditional, so a freshly spun vertical - whose neutral shell leaves
   ORA.scope.verticalId null by design until it hydrates - silently read the
   PILOT's persona, desk and capability rows. The dock wore Conduct Connect's
   identity on someone else's URL. The constant now applies ONLY on a bare
   pilot route; under /v/<slug>/ or /c/<slug>/ an unresolved tenant returns
   null and every caller below skips its read rather than loading another
   tenant's config. Null is the honest answer, and each reader already has an
   honest empty state for it. */
function activeVerticalId(): string | null {
  try {
    const env: any = (window as any).__ORBI_ENV__ || {};
    if (typeof env.ORBI_VERTICAL_ID === 'string' && env.ORBI_VERTICAL_ID) return env.ORBI_VERTICAL_ID;
    const cfg = env.ORBI_SCOPE && typeof env.ORBI_SCOPE === 'object' ? env.ORBI_SCOPE : null;
    if (cfg && typeof cfg.vertical_id === 'string' && cfg.vertical_id) return cfg.vertical_id;
    const ora: any = (window as any).ORA;
    if (ora?.scope?.verticalId) return String(ora.scope.verticalId);
  } catch { /* fall through to the fail-closed branch below */ }
  return tenantUnresolved() || urlAltitude() ? null : TEGLO_VERTICAL_ID;
}

/* The platform orb is ALWAYS Orbi in operator mode, never a customer/NOC role.
   The subtitle is derived from the active lens at render time (see derivedIdentity),
   never hardcoded. This constant is only the neutral fallback before scope reads. */
const PLATFORM_IDENTITY = { title: 'AI assistant', subtitle: 'Operator' };
/* Default voice persona for an opt-in call. Sarah support voice, never NOC. */
const DEFAULT_VOICE_PERSONA = 'customer_support';

/* Panel state is keyed to the OPERATOR SESSION (Phase 3), so continuity follows
   the operator's browser session and a future authenticated shell can swap in a
   real operator id without a storage migration. v1 was a global slot; it is
   read once as a fallback so an in-flight session upgrades losslessly. */
const OPERATOR_SID = operatorSessionId();
const PERSIST_KEY = `orbi_panel_state_v2:${OPERATOR_SID}`;
const LEGACY_PERSIST_KEY = 'orbi_panel_state_v1';

/**
 * GENERATIVE UI flag (JARVIS-PLATFORM-ORB Phase 1). OFF by default; flip via
 * window.__ORBI_ENV__.ORBI_GENERATIVE_UI = true or
 * localStorage.setItem('orbi_generative_ui', '1'). Read at ask time so toggling
 * needs no reload. When off, the ask request never opts in (the server then
 * emits no ui) and nothing new renders: a kill switch at both ends.
 */
function generativeUiOn(): boolean {
  try {
    if ((window as any).__ORBI_ENV__?.ORBI_GENERATIVE_UI === true) return true;
    return localStorage.getItem('orbi_generative_ui') === '1';
  } catch { return false; }
}

/**
 * JARVIS J1 voice-navigation flag. OFF by default; flip via
 * window.__ORBI_ENV__.ORBI_JARVIS_NAV = true or
 * localStorage.setItem('orbi_jarvis_nav', '1'). Read at ask time so toggling
 * needs no reload. When off, the ask request never opts in and the server
 * classifier never runs: a kill switch at both ends, same as generative UI.
 */
function jarvisNavOn(): boolean {
  try {
    if ((window as any).__ORBI_ENV__?.ORBI_JARVIS_NAV === true) return true;
    return localStorage.getItem('orbi_jarvis_nav') === '1';
  } catch { return false; }
}

const __ORBI_ENV__: any =
  (typeof window !== 'undefined' && (window as any).__ORBI_ENV__) || {};
/* DROP-IN (Phase 3 binding 4): config-only; no hardcoded project fallback. */
const SUPABASE_URL =
  __ORBI_ENV__.SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY =
  __ORBI_ENV__.SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || '';

const MIC_PREFLIGHT_KEY = 'orbi_mic_permission_seen';

type OrbVisualState = 'idle' | 'listening' | 'active' | 'escalation' | 'reconnecting';

interface VoicePersona {
  persona_key: string;
  title: string;
  subtitle?: string;
  greeting?: string;
  conversation_starters?: string[];
}

interface CustomerPriming {
  id?: string;
  name?: string;
  phone?: string;
  account_number?: string;
  email?: string;
}

/* JARVIS RESOLVE: the situation the network Resolve control primes the dock with.
   The PAGE owns this truth (it already holds the down set and the active outage
   events); the dock only carries it into the briefing and the voice context. */
interface SituationPriming {
  source?: string;
  section?: string;
  client_slug?: string;
  client_name?: string;
  down_count?: number;
  down_sample?: Array<{ name?: string | null; account_number?: string | null; building?: string | null }>;
  outage_event?: {
    id?: string; area_key?: string; area_label?: string | null; down_count?: number;
    total_in_area?: number | null; first_detected_at?: string | null;
  } | null;
  resolve_board_href?: string;
}

/* JARVIS RESOLVE, elegant model (round 4 item 2): after navigating to the
   Resolve board, its window.OraJarvisResolve bridge is not there yet - the
   framed page needs a beat to load and register it (same shape as
   waitForSituation in situation.ts). Polls the workspace-aware window (the
   iframe when the layer is up, else the bare window) until the bridge
   appears or the timeout passes. */
async function waitForResolveBridge(timeoutMs = 6000): Promise<any> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const w: any = workspaceWindow() || window;
    // CRITICAL: the network page ALSO defines window.OraJarvisResolve (a modal-
    // based one). Right after navigating the frame network -> resolve, the frame
    // still shows network for a beat, so a bare bridge check would grab the wrong
    // page's bridge and drive its modal (which then vanishes as the frame loads
    // resolve). Only trust the bridge once the frame is actually ON the resolve
    // route AND the resolve board's own list section exists in its document.
    try {
      const onResolve = /\/resolve(\?|$|\/)/.test(String(w?.location?.pathname || '') + String(w?.location?.search || ''));
      const hasBoard = !!w?.document?.getElementById?.('mrSection');
      if (onResolve && hasBoard && w.OraJarvisResolve && typeof w.OraJarvisResolve.openConfirm === 'function') {
        return w.OraJarvisResolve;
      }
    } catch { /* frame mid-navigation, not readable yet */ }
    await new Promise((res) => setTimeout(res, 150));
  }
  return null;
}

/* One factual situation line, shared by the voice-context stamp and the mid-call
   sync message, so the agent and the operator always hear the same numbers. */
function describeSituation(sit: SituationPriming): string {
  const ev = sit.outage_event || null;
  const who = sit.client_name ? ` for ${sit.client_name}` : '';
  const down = typeof sit.down_count === 'number' ? `${sit.down_count} connection(s) down` : 'connections down';
  const names = Array.isArray(sit.down_sample) && sit.down_sample.length
    ? ` Among the down: ${sit.down_sample.slice(0, 5).map((d) => d.name || d.account_number).filter(Boolean).join(', ')}.`
    : '';
  const evLine = ev
    ? ` Active outage event: ${ev.area_label || ev.area_key}${ev.down_count != null ? ` — ${ev.down_count}${ev.total_in_area ? ` of ${ev.total_in_area}` : ''} down` : ''}${ev.first_detected_at ? ` since ${String(ev.first_detected_at).slice(0, 16).replace('T', ' ')} UTC` : ''}.`
    : ' No active outage event, so the batch sweep is not armed; individual resolves run from the Resolve board.';
  return `The operator opened this from the network Resolve control${who}: ${down}.${names}${evLine}`;
}

interface OrbiDockHandle {
  setCustomer: (c: CustomerPriming | null) => void;
  setPersona: (key: string) => void;
  open: () => void;
  close: () => void;
  callCustomer: (c: CustomerPriming | null) => void;
  showCard: (cardType: string, payload?: OrbiCardModel['payload']) => void;
  /** JARVIS RESOLVE: open the panel primed with the outage/resolve situation. */
  openSituation: (sit: SituationPriming) => void;
}

interface OrbiDockProps {
  registerHandle?: (h: OrbiDockHandle) => void;
}

/* DROP-IN (Phase 3 binding 4): with no configured project the module must
   still LOAD - createClient throws on an empty URL, and a module-scope throw
   kills the whole mount. A null client leaves every catalogue read dark
   (personas, voices, capabilities, desks) while the dock itself still mounts
   and says what it cannot do. */
/* SESSION BEARER (RLS audit 2026-09-01).
   ------------------------------------
   This client used to send the bare publishable key, so every catalogue read
   below reached PostgREST as role `anon`. That was invisible only because the
   sibling tables were already `{authenticated}` and quietly returned nothing:
   the one read that DID work, jarvis_capabilities, worked because its policy
   still carried `anon` + `USING (true)`, i.e. the hole this audit closes.

   Same resolver contextFetcher.ts already uses: ORA.accessToken() goes through
   supabase-js getSession() and therefore REFRESHES an expired token instead of
   scraping a stale one out of localStorage. supabase-js calls this per request,
   so a token that rolls mid-session is picked up without remounting the dock.

   Falls back to the publishable key when there is no session (the standalone
   voice surface carries no window.ORA). That is deliberately NOT a bypass: with
   the policies now `{authenticated}` and tenant-scoped, an anon bearer reads
   zero rows, which is the honest outcome for a caller who has not signed in. */
async function tegloSessionBearer(): Promise<string> {
  try {
    const ora: any = (typeof window !== 'undefined' && (window as any).ORA) || null;
    if (ora && typeof ora.accessToken === 'function') {
      const tok = await ora.accessToken();
      if (tok) return tok;
    }
  } catch { /* no session resolver on this surface: anon bearer, zero rows */ }
  return SUPABASE_ANON_KEY;
}

/* THE LENS RIDES ON THE DOCK'S OWN CLIENT TOO (TENANT-1, T1). This client is not
   wrapped by ORA.scopeClient, so its reads declared no vertical and a platform
   owner's dock read whatever the session could see. The shell's own derivation
   (ORA.lensHeaders, read at call time so a tenant that resolves after mount is
   still named) is put on every request here; a page with no shell sends none,
   exactly as before. */
function lensedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers((init && init.headers) || {});
  try {
    const ora: any = (window as any).ORA;
    const lh = ora && typeof ora.lensHeaders === 'function' ? ora.lensHeaders() : null;
    if (lh) for (const k of Object.keys(lh)) { if (lh[k] !== undefined && lh[k] !== null) headers.set(k, String(lh[k])); }
  } catch { /* no shell: no lens */ }
  return fetch(input, { ...(init || {}), headers });
}
const sbVoicePersonas = SUPABASE_URL && SUPABASE_ANON_KEY
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: { persistSession: false, autoRefreshToken: false },
      accessToken: tegloSessionBearer,
      global: { fetch: lensedFetch },
    })
  : null;

let askSeq = 0;
const nextId = () => `ask-${Date.now()}-${++askSeq}`;

/* ONE INTENT, ONE CARD (2026-08-15).
   -----------------------------------
   The operator sees the "Let the AI call X" card three times for one subject and
   reports it as a triple dispatch. It is not: every producer appends exactly once
   per invocation, and there is a single mount, a single panel, a single form and a
   single onAsk (checked across entry.tsx, OrbiDock.tsx, VoiceOverlayPanel.tsx and
   the host pages). What actually stacks them is that askLog is PERSISTENT and the
   appends are UNCONDITIONAL:

     * askLog is mirrored to sessionStorage on every change and restored on mount
       (loadPersisted/savePersisted, and the `restored` ref), deliberately, so a
       call and a typed exchange read as one conversation across page hops. This
       shell is multi-page — every navigation is a real document load — so each hop
       replays the whole log.
     * Both call-card producers do `setAskLog(prev => [...prev, …])` with no notion
       of the subject already being on screen. So re-opening the drawer on the same
       customer and pressing Call again — or pressing it, hopping a page, and
       pressing it again — leaves one more identical card each time, permanently.
     * appendOrbi has the same gap for text, which is why the error card multiplies
       alongside it: the same failing action re-run appends the same sentence again.
       useVapi's addLog already dedupes consecutive identical voice lines for
       exactly this reason; the typed/card path never got the equivalent.

   So the invariant is enforced where the invariant lives — at the append. A repeat
   of the same subject intent REPLACES its earlier card rather than stacking a
   second one (the newest is what the operator is acting on, so it stays at the
   bottom of the thread), and an identical consecutive orbi line is dropped. This
   holds however many times a dispatch fires, which also makes the panel correct
   against a stale cached bundle or a host page that calls callCustomer twice —
   neither of which the panel can see or control. */
/* account_number FIRST: the two producers populate the fields differently (the
   name search always has the uuid, the drawer priming sometimes only the account
   number), so an id-first key gave the same human two different keys and the
   dedup missed (audit A5). The account number is the one field both carry. */
const callCardKey = (opts: NonNullable<AskEntry['callOptions']>) =>
  opts.map((o) => o.account_number || o.account_id || o.phone || o.name || '?').join('|');

/** Drop any earlier card offering the same subject set, so the repeat is a move, not a copy. */
const withoutPriorCallCard = (prev: AskEntry[], key: string) =>
  prev.filter((e) => !(e.callOptions && e.callOptions.length > 0 && callCardKey(e.callOptions) === key));

/* Subject key of a generative subscriber_card block (registry-validated shape). */
const uiSubjectKey = (b: UiBlock): string | null => {
  const p = (b as { props?: { account_number?: string; heading?: string } }).props || {};
  return p.account_number || p.heading || null;
};

/** THREAD-WIDE one-card invariant (audit A5). The #296 invariant guarded only
    callOptions at two append sites, while the customers panel's duplicates come
    from generative subscriber_card ui blocks (appended bare at the ask site) and
    from whole-thread merges (the sessionStorage restore and the "view previous"
    splice) that never dedup at all. This walks the thread once, newest first:
    a repeated call card drops its older twin, a repeated subscriber_card ui
    block for the same subject is stripped from the older entry (its text
    stays — history is kept, the twin card is not). */
const dedupeThread = (entries: AskEntry[]): AskEntry[] => {
  const seenCall = new Set<string>();
  const seenUi = new Set<string>();
  const out: AskEntry[] = [];
  for (let i = entries.length - 1; i >= 0; i--) {
    let e = entries[i];
    if (e.callOptions && e.callOptions.length > 0) {
      const k = callCardKey(e.callOptions);
      if (seenCall.has(k)) continue;
      seenCall.add(k);
    }
    if (e.ui && e.ui.length) {
      const kept = e.ui.filter((b) => {
        if (b.type !== 'subscriber_card') return true;
        const k = uiSubjectKey(b);
        if (!k) return true;
        if (seenUi.has(k)) return false;
        seenUi.add(k);
        return true;
      });
      if (kept.length !== e.ui.length) e = { ...e, ui: kept.length ? kept : undefined };
    }
    out.push(e);
  }
  return out.reverse();
};

/* Map a persona_voices row to the voice shape useVapi passes to Vapi. persona_voices
   is the single source of truth for the talk-mode voice (architectural_truth rule 42). */
function toVoiceConfig(row: any) {
  if (!row || !row.voice_id) return null;
  const s = row.settings || {};
  return {
    voiceId: row.voice_id,
    model: row.voice_model || 'eleven_flash_v2_5',
    stability: s.stability,
    similarityBoost: s.similarity_boost,
    style: s.style,
    useSpeakerBoost: s.use_speaker_boost,
    speed: s.speed,
  };
}

/* ── sessionStorage persistence helpers (item 4 + Phase 3 continuity + the
   UX-pass session model) ──
   The thread survives a PAGE HOP (different pathname: the operator is moving
   through the app mid-conversation, Phase 3 continuity) but a REFRESH of the
   same page starts a FRESH chat with the old thread parked behind a subtle
   "view previous" affordance, and everything expires after a few idle hours.
   The primed SUBJECT is deliberately NOT restored: a fresh page load never has
   a subscriber panel open, so the subject re-primes only when the operator
   explicitly opens one (Eri review item 3: never auto-pick). */
const CHAT_IDLE_EXPIRY_MS = 4 * 60 * 60 * 1000;
/* A3 ROOT CAUSE FIX: the persisted `path` must be the DOCUMENT THIS CODE LOADED
   IN, captured once at module load — never window.location at save time. The
   workspace's in-place navigation pushState()es the DESTINATION route onto the
   old document's history, so a save-time read stamped the destination; when the
   call ended and the real navigation landed, loadPersisted saw path === here,
   misread the arrival as a refresh of a page it never loaded, and parked the
   whole thread — the dock then "forgot" the navigation it had just performed
   and denied it could navigate at all. */
const DOC_PATH = window.location.pathname + window.location.search;
interface PersistedState {
  isOpen: boolean;
  askLog: AskEntry[];
  /** the prior thread, parked on refresh, restorable via "view previous" */
  prevLog: AskEntry[];
  /** the envelope trail: sections visited this operator session (Phase 3) */
  trail: TrailEntry[];
}
function loadPersisted(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY) ?? sessionStorage.getItem(LEGACY_PERSIST_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    const askLog: AskEntry[] = Array.isArray(p.askLog) ? p.askLog.slice(-100) : [];
    const prevLog: AskEntry[] = Array.isArray(p.prevLog) ? p.prevLog.slice(-100) : [];
    const trail = Array.isArray(p.trail) ? p.trail.slice(-8) : [];
    const at = typeof p.at === 'number' ? p.at : 0;
    // Idle expiry: an old thread never greets the operator days later.
    if (!at || Date.now() - at > CHAT_IDLE_EXPIRY_MS) {
      return { isOpen: false, askLog: [], prevLog: [], trail: [] };
    }
    // Same pathname AND query = a deliberate refresh: fresh chat, thread
    // parked. A hop anywhere else (the MPA changes pathname; the harness
    // changes the query) continues the thread (Phase 3 continuity).
    const here = window.location.pathname + window.location.search;
    const samePath = typeof p.path === 'string' && p.path === here;
    if (samePath && askLog.length > 0) {
      return { isOpen: !!p.isOpen, askLog: [], prevLog: dedupeThread([...prevLog, ...askLog]).slice(-100), trail };
    }
    // Different pathname = a page hop mid-conversation: the thread continues.
    // dedupeThread: the restore path never deduped, so a replayed thread could
    // resurrect twins the append-site invariant had already collapsed (A5).
    return { isOpen: !!p.isOpen, askLog: dedupeThread(askLog), prevLog, trail };
  } catch { return null; }
}
function savePersisted(state: PersistedState) {
  try {
    sessionStorage.setItem(PERSIST_KEY, JSON.stringify({
      isOpen: state.isOpen,
      askLog: state.askLog.slice(-100),
      prevLog: state.prevLog.slice(-100),
      trail: state.trail.slice(-8),
      path: DOC_PATH, // the loaded document, immune to workspace pushState (A3)
      at: Date.now(),
    }));
    sessionStorage.removeItem(LEGACY_PERSIST_KEY);
  } catch { /* storage full or blocked, persistence is best-effort */ }
}

export const OrbiDock: React.FC<OrbiDockProps> = ({ registerHandle }) => {
  const restored = useRef(loadPersisted());
  const [isOpen, setIsOpen] = useState(restored.current?.isOpen ?? false);
  const [personas, setPersonas] = useState<VoicePersona[]>([]);
  const [personaIndex, setPersonaIndex] = useState(0);
  /* The subject primes ONLY from an explicitly open subscriber panel (via the
     window bridge) - never restored from storage, never auto-picked. */
  const [customer, setCustomer] = useState<CustomerPriming | null>(null);
  const [showMicHint, setShowMicHint] = useState(false);

  const [askLog, setAskLog] = useState<AskEntry[]>(restored.current?.askLog ?? []);
  const [prevLog, setPrevLog] = useState<AskEntry[]>(restored.current?.prevLog ?? []);
  const [askPending, setAskPending] = useState(false);
  const [callPending, setCallPending] = useState(false);
  const [platform, setPlatform] = useState<PlatformContext | null>(null);
  /* Talk-mode voice, resolved from persona_voices (rule 42 source of truth). */
  const [voiceConfig, setVoiceConfig] = useState<any>(null);

  const sessionInflightRef = useRef(false);

  /* Generative cards the voice agent spins up via the show_card tool. Kept to the
     last few; ephemeral (tied to the live session), so not persisted. */
  const [cards, setCards] = useState<OrbiCardModel[]>([]);
  const pushCard = useCallback((cardType: string, payload?: OrbiCardModel['payload']) => {
    setCards((prev) => [...prev, { id: nextId(), type: cardType, payload: payload || {} }].slice(-4));
  }, []);

  /* Registry blocks the voice agent spins up via show_blocks (Phase 4 voice
     parity). Validated through the SAME client registry as the typed ask path
     before anything paints; ephemeral like the cards. */
  const [voiceBlocks, setVoiceBlocks] = useState<UiBlock[]>([]);
  const onShowBlocks = useCallback((raw: unknown) => {
    // report_link is server-composed only (Phase 6); the voice agent cannot
    // generate a report file, so a voice-spun one would be an empty claim.
    const blocks = validateUiBlocks(raw).filter((b) => b.type !== 'report_link').slice(0, 2);
    setVoiceBlocks(blocks);
    return { types: blocks.map((b) => b.type) };
  }, []);

  /* Subject-by-voice (round 2 item 6): the operator NAMES a subscriber, the
     agent calls lookup_subscriber, this resolves via the same lens-scoped
     search as the typed path, primes the subject, and returns the FULL live
     record. Multiple matches return candidates; none returns an honest miss. */
  const onLookupSubscriber = useCallback(async (name: string) => {
    const ctx = readPlatformContext();
    let cands: Awaited<ReturnType<typeof searchSubscribersByName>>;
    try {
      cands = await searchSubscribersByName(ctx, name);
    } catch (err) {
      // A signed-out or expired operator used to reach the "no subscriber
      // matching" line below, because anon got 200 + [] from RLS. The agent
      // then told them the person did not exist. Say what actually happened.
      const authText = operatorAuthFailureText(err);
      if (authText) return { found: false, note: `${authText} Tell the operator this verbatim; do not say the subscriber could not be found.` };
      console.warn('[OrbiDock] lookup_subscriber failed:', err);
      return { found: false, note: 'The subscriber lookup did not respond. Say so plainly with no technical detail.' };
    }
    if (cands.length === 0) return { found: false, note: `No subscriber matching "${name}" in this lens. Ask the operator to check the name or give the account number.` };
    if (cands.length > 1) {
      return { found: false, candidates: cands.slice(0, 4).map((c) => ({ name: c.name, account_number: c.account_number, status: c.status, is_test: c.is_test })) };
    }
    const c = cands[0];
    setCustomer({ id: c.account_id, name: c.name, account_number: c.account_number || undefined, phone: c.phone || undefined });
    const detail: any = c.account_number ? await fetchSubjectDetail(ctx, c.account_number).catch(() => null) : null;
    // Trust honesty: a score exists only for subscribers Orbi has real
    // conversation history with; say so gracefully instead of "not available".
    if (detail && detail.trust_confidence == null) {
      detail.trust_note = 'No conversation history yet, so no trust score has formed for this subscriber. It builds as calls and chats land.';
    }
    const subscriber = detail ?? { account_number: c.account_number, name: c.name, status: c.status, is_test: c.is_test };
    // RENDER DETERMINISTICALLY (round 3 item 3): don't depend on the model
    // separately calling show_blocks after a successful lookup - paint the
    // subscriber card here, in code, the moment the resolve lands.
    pushCard('subscriber', subscriber);
    return { found: true, primed: true, subscriber };
  }, [pushCard]);

  /* ONE-BRAIN (JARVIS NAV): the voice agent's jarvis_navigate tool routes the
     operator's SPOKEN command through the SAME deterministic classifier the
     typed path uses (orbi-operator-ask + jarvis_nav, classify_only). The impl
     is defined below (it needs the ask/navigate callbacks); this stable
     wrapper hands useVapi one function identity for the life of the dock. */
  const jarvisCommandImplRef = useRef<(utterance: string) => Promise<any>>(async () => ({ matched: false }));
  const onJarvisCommand = useCallback((utterance: string) => jarvisCommandImplRef.current(utterance), []);

  /* JARVIS SITUATIONAL AWARENESS: the tenant's capability catalogue, read once
     per mount from jarvis_capabilities (config, not code) and resolved against
     the active client lens at use time (a client-scoped row overrides or
     disables the vertical-wide one). An absent table or a failed read yields
     an empty catalogue - the feature honestly stays dark, nothing guesses. */
  const capsRawRef = useRef<any[] | null>(null);
  const loadCapabilityRows = useCallback(async (): Promise<any[]> => {
    if (capsRawRef.current) return capsRawRef.current;
    try {
      const vid = activeVerticalId();
      /* An unresolved tenant has no catalogue to read. Empty, not the pilot's. */
      if (!sbVoicePersonas || !vid) { capsRawRef.current = []; return capsRawRef.current; }
      const { data, error } = await sbVoicePersonas
        .from('jarvis_capabilities')
        .select('capability_key,title,offer_line,tool_names,section,requires,enabled,client_id,sort_order')
        .eq('vertical_id', vid)
        .order('sort_order', { ascending: true });
      capsRawRef.current = !error && Array.isArray(data) ? data : [];
    } catch {
      capsRawRef.current = [];
    }
    return capsRawRef.current;
  }, []);
  const resolvedCapabilities = useCallback(async (ctx: PlatformContext) => {
    return applyTenantOverrides(await loadCapabilityRows(), ctx.scope.client_id);
  }, [loadCapabilityRows]);

  /* Mass-resolve voice face (capability tools). The SAME governed endpoint the
     network page's confirm modal uses. Preview: zero writes, paints the
     numbers beside the voice, hands the agent the honest summary. Fire: the
     operator's explicit on-screen confirm is REQUIRED before the relay; the
     server then re-computes the selection under its own gates (dedup, test
     mode allowlist, quiet hours). No outage event on screen = an honest
     not-available result, never a guess. */
  const activeOutage = useCallback(() => {
    const ctx = readPlatformContext();
    return { ctx, events: readOutageEvents(ctx) };
  }, []);
  const onResolvePreview = useCallback(async () => {
    if (!jarvisNavOn() || !generativeUiOn()) return { available: false, note: 'Mass resolve is not enabled in this console.' };
    const { ctx, events } = activeOutage();
    if (!events.length) {
      return { available: false, note: 'No active outage event is on this screen. Mass resolve runs while an outage event is active; offer to open the Resolve board if the operator wants it.' };
    }
    const ev = events[0];
    // INLINE REVIEW: paint a short summary + confirm button in the voice panel
    // too, but the FULL list (round 4 item 2) lives on the Resolve board now -
    // no dock modal, ever.
    const paintInline = (r: any) => {
      if (!r || !r.ok) return;
      const exc = r.excluded || {};
      const excludedTotal = (exc.suspended_nonpayment || 0) + (exc.never_provisioned || 0) + (exc.no_phone || 0) + (exc.already_called || 0);
      const blocks = validateUiBlocks([
        {
          type: 'metric_chips',
          props: {
            heading: `Mass resolve preview - ${r.outage?.area || 'outage area'}`,
            chips: [
              { label: 'Will be called', value: String(r.will_call || 0), variant: r.will_call ? 'warn' : 'muted' },
              { label: 'Excluded', value: String(excludedTotal), variant: 'muted' },
              { label: 'Property notes', value: String(exc.bulk_mdu_property_groups || 0), variant: 'muted' },
              { label: 'Mode', value: r.test_mode ? 'TEST' : 'LIVE', variant: r.test_mode ? 'ok' : 'danger' },
            ],
          },
        },
        {
          type: 'options',
          props: { heading: 'The full list, with every exclusion and its reason, is on the Resolve board' },
          actions: [{ label: r.test_mode ? 'Confirm & fire (test mode)' : 'Confirm & fire', action: 'resolve_fire', params: {} }],
        },
      ]);
      if (blocks.length) setVoiceBlocks(blocks);
    };
    // Already on the Resolve board: its bridge is right here, drive it directly.
    const onResolveBoard = ctx.scope.section === 'resolve';
    // Same priority as onResolveFire: the workspace frame (if the layer is up
    // and framing resolve.html) is the real board; bare window is only right
    // when resolve.html itself is the top-level document (no frame active).
    const directBridge = onResolveBoard
      ? (((workspaceWindow() as any)?.OraJarvisResolve) || (window as any).OraJarvisResolve)
      : null;
    if (directBridge && typeof directBridge.openConfirm === 'function') {
      try {
        const r = await directBridge.openConfirm(ev.id);
        paintInline(r);
        return r ?? { ok: false, note: 'The preview did not open.' };
      } catch (err) {
        console.warn('[OrbiDock] resolve bridge preview failed:', err);
        return { ok: false, note: 'The resolve preview did not respond; say so plainly, with no technical detail.' };
      }
    }
    // Anywhere else: navigate to the Resolve board (in place, mid-call safe)
    // with the outage event named in the URL. The board renders the full list
    // ITSELF on boot from that param (round 5) - it does NOT depend on this
    // openConfirm reaching the bridge, so the list is always up. openConfirm is
    // then a best-effort read of the exact counts back to the voice.
    const href = ctx.scope.client_slug
      ? `/c/${ctx.scope.client_slug}/resolve?outage_event=${encodeURIComponent(ev.id)}`
      : `/v/${ctx.scope.vertical_slug || 'isp'}/resolve?outage_event=${encodeURIComponent(ev.id)}`;
    const nav = navigateRoute(href, { voice: true });
    if (!nav.moved) {
      return { available: true, ok: false, note: `Could not open the Resolve board (${nav.reason}); say so plainly.` };
    }
    // SPEECH SYNC (round 5, same law as nav): the board IS opening and WILL show
    // the full who-gets-called list - so the spoken outcome must be positive.
    // NEVER apologise for "not showing" it: it is showing. If the bridge answers
    // with the exact counts, hand those to the voice; if it is just slow, still
    // return a positive "the list is on the Resolve board now" - the operator is
    // looking at it either way. No failure/apology path once the nav succeeded.
    const bridge = await waitForResolveBridge(10000);
    if (bridge) {
      try {
        const r = await bridge.openConfirm(ev.id);
        if (r && r.ok) {
          paintInline(r);
          return r;
        }
      } catch (err) {
        console.warn('[OrbiDock] resolve bridge preview read failed (list still renders on the board):', err);
      }
    }
    // Bridge slow or the count read hiccuped, but the board is open and rendering
    // the list from the URL. Tell the operator to review it there - never that it
    // failed to show.
    return {
      available: true, ok: true, opened_on_board: true,
      note: 'The Resolve board is open now with the full who-gets-called list - included customers and every excluded group with its reason. Tell the operator it is on their screen to review, then offer to fire on their say-so. Do NOT apologise or say the list did not show; it is showing.',
    };
  // navigateRoute is intentionally NOT a dep: it's declared later in this
  // component (its own identity is stable - useCallback([])), and listing it
  // here would reference the binding before initialization at render time
  // (TDZ) since deps arrays are evaluated eagerly, unlike the callback body.
  }, [activeOutage]);
  const onResolveFire = useCallback(async () => {
    if (!jarvisNavOn() || !generativeUiOn()) return { fired: false, note: 'Mass resolve is not enabled in this console.' };
    // show_resolve_preview always runs first (the system prompt requires it),
    // so the Resolve board's bridge should already be loaded. Resolve the
    // RESOLVE-page bridge specifically (route + #mrSection guarded, same as
    // waitForResolveBridge) so we never drive the network page's own modal
    // bridge by accident. Its window.confirm() is the one fire-confirm gate.
    const resolveBridge = (() => {
      const w: any = workspaceWindow();
      try {
        if (w && /\/resolve(\?|$|\/)/.test(String(w.location?.pathname || '') + String(w.location?.search || '')) && w.document?.getElementById?.('mrSection') && w.OraJarvisResolve?.fire) return w.OraJarvisResolve;
      } catch { /* frame not readable */ }
      // Top-level document is the resolve board itself (no workspace frame up).
      try {
        if (/\/resolve(\?|$|\/)/.test(window.location.pathname + window.location.search) && document.getElementById('mrSection') && (window as any).OraJarvisResolve?.fire) return (window as any).OraJarvisResolve;
      } catch { /* ignore */ }
      return null;
    })();
    const bridge = resolveBridge;
    if (bridge && typeof bridge.fire === 'function') {
      try {
        const r = await bridge.fire();
        return r ?? { fired: false, note: 'The fire path returned nothing.' };
      } catch (err) {
        console.warn('[OrbiDock] resolve bridge fire failed:', err);
        return { fired: false, note: 'The fire step did not respond; say so plainly, with no technical detail.' };
      }
    }
    return { fired: false, note: 'The Resolve board is not open yet; ask the operator to say "resolve it" again so the preview opens first.' };
  }, []);

  /* JARVIS RESOLVE: the situation primed by the network Resolve control. Never
     persisted (a reload re-primes from the live page, same law as the subject);
     a ref mirror keeps the stable voice callbacks reading the current truth. */
  const [situation, setSituation] = useState<SituationPriming | null>(null);
  const situationRef = useRef<SituationPriming | null>(null);
  situationRef.current = situation;

  const {
    connectionState,
    agentState,
    audioLevel,
    logs,
    startCall,
    stopCall,
    sendMessage,
    sendContext,
    micError,
  } = useVapi(pushCard, onShowBlocks, onLookupSubscriber, onJarvisCommand, onResolvePreview, onResolveFire);

  const isConnected  = connectionState === ConnectionState.CONNECTED;
  const isConnecting = connectionState === ConnectionState.CONNECTING;

  /* JARVIS NAV: the live-call signal the navigation callbacks read. A ref, so
     the stable callbacks (navigateRoute, the voice tool handler) see the
     current truth without re-binding on every connection change. */
  const voiceAliveRef = useRef(false);
  voiceAliveRef.current = isConnected || isConnecting;

  /* Voice-spun blocks live with the call: gone the moment it disconnects.
     JARVIS NAV: the in-place workspace also commits here - the call is over,
     so the MPA returns to one pristine document at the route on screen. */
  useEffect(() => {
    if (connectionState === ConnectionState.DISCONNECTED || connectionState === ConnectionState.ERROR) {
      setVoiceBlocks([]);
      // SPEECH SYNC: a voice navigation the workspace could not take in place
      // was DEFERRED (and the agent said so); honour that promise now. The
      // deferred screen is the operator's newest ask, so it wins over a plain
      // workspace commit.
      const deferred = deferredVoiceNavRef.current;
      if (deferred) {
        deferredVoiceNavRef.current = null;
        window.setTimeout(() => { if (!voiceAliveRef.current) navigateRoute(deferred); }, 600);
      } else if (workspaceActive()) {
        // A beat so the goodbye and the panel settle before the document load.
        // Re-check at fire time: if a NEW call started in the meantime, the
        // commit would drop it - hold the workspace until that call ends too.
        window.setTimeout(() => { if (!voiceAliveRef.current) commitWorkspace(); }, 600);
      }
    }
  }, [connectionState]);

  /* Mid-call subject sync (round 2 item 8): the voice prompt is fixed at call
     start, so opening or closing the subscriber panel DURING a live call must
     re-point the agent explicitly - otherwise it stays stuck on the closed
     subscriber. Closing drops the subject; opening announces the new one.
     JARVIS SITUATIONAL AWARENESS: with the nav flag on, the open announcement
     carries the subscriber's live STATE (status, billing, connection) so the
     agent discusses them before its first lookup; flag off keeps the original
     message byte for byte. */
  const prevSubjectRef = useRef<string | null>(null);
  useEffect(() => {
    const now = customer?.account_number || customer?.name || null;
    const prev = prevSubjectRef.current;
    prevSubjectRef.current = now;
    if (!isConnected || now === prev) return;
    try {
      if (!now) {
        sendMessage('The operator CLOSED the subscriber panel. Drop that subject entirely: do not bring them up again unless the operator re-opens or names them. Revert to page-level context.');
      } else {
        const who = customer?.name || `account ${customer?.account_number}`;
        sendMessage(`The operator opened ${who}${customer?.account_number ? ` (account ${customer.account_number})` : ''} in a panel; they are now the subject. Call lookup_subscriber with "${customer?.account_number || customer?.name}" if you need their live record.`);
        // The announcement above never waits on a read; the subscriber's live
        // STATE follows as a silent context message when the rows land.
        const acct = customer?.account_number;
        if (jarvisNavOn() && acct) {
          void (async () => {
            try {
              const d = await fetchSubjectDetail(readPlatformContext(), acct);
              if (!d || (customer?.account_number || customer?.name || null) !== prevSubjectRef.current) return;
              const bits: string[] = [];
              if (d.status) bits.push(`status ${d.status}`);
              if (d.billing_status) bits.push(`billing ${d.billing_status}`);
              if (d.connection_status) bits.push(`connection ${d.connection_status}`);
              if (typeof d.open_ticket_count === 'number') bits.push(`${d.open_ticket_count} open ticket${d.open_ticket_count === 1 ? '' : 's'}`);
              if (bits.length) sendContext(`SUBJECT STATE (hold this context; reply only when the operator speaks): ${who} right now: ${bits.join(', ')}.`);
            } catch { /* the state line is additive; the announcement already landed */ }
          })();
        }
      }
    } catch { /* no live channel */ }
  }, [customer, isConnected, sendMessage, sendContext]);

  /* JARVIS SITUATIONAL AWARENESS: EVERY in-place navigation re-briefs the
     agent BEFORE the operator speaks. The workspace layer notifies on each
     frame load (jarvis navigations and in-frame link clicks alike); this
     waits for the new page's envelope to land, then hands the agent the
     situation - page identity, client lens, salient live data - plus the
     capabilities available THERE, as a silent context message. Gated on
     ORBI_JARVIS_NAV (the layer itself only exists under that flag) and a
     live call; a newer navigation supersedes an in-flight brief. */
  const primeSeqRef = useRef(0);
  /* LIVE ENVELOPE (2026-08-26): the last situation brief actually sent to the
     agent, shared by the nav re-brief and the envelope-change re-brief below
     so the same screen state is never spoken to the model twice. */
  const lastBriefRef = useRef<string>('');
  useEffect(() => {
    setWorkspaceNavListener((route: string) => {
      if (!jarvisNavOn() || !voiceAliveRef.current) return;
      const seq = ++primeSeqRef.current;
      let expect: string | null = null;
      try {
        const segs = route.split('?')[0].split('/').filter(Boolean);
        expect = segs.length ? segs[segs.length - 1] : null;
      } catch { /* section unknown; the wait falls back to any data */ }
      void (async () => {
        const ctx = await waitForSituation(expect);
        if (seq !== primeSeqRef.current || !voiceAliveRef.current) return;
        let capBlock = '';
        try {
          if (generativeUiOn()) {
            const caps = capabilitiesHere(await resolvedCapabilities(ctx), ctx, { generative_ui: true });
            if (caps.length) capBlock = `\nCapabilities available on THIS screen (offer by name when the operator's ask matches):\n${capabilityLines(caps, true)}`;
          }
        } catch { /* capability lines are additive */ }
        try {
          // FAIL-SOFT: situationBrief never throws now; when the envelope is
          // empty it returns '', so with no capabilities either there is simply
          // nothing to brief. Send only when there is real context - an absent
          // pre-context degrades to silence, never a spoken error.
          const brief = situationBrief(ctx);
          if (brief || capBlock) {
            lastBriefRef.current = brief;
            sendContext(`SCREEN UPDATE (hold this context; reply only when the operator speaks): ${brief}${capBlock}`);
          }
        } catch { /* no live channel */ }
      })();
    });
    return () => setWorkspaceNavListener(null);
  }, [sendContext, resolvedCapabilities]);

  /* LIVE ENVELOPE (2026-08-26): the envelope publisher has always dispatched
     an ora:context event on every recompute (a data load, a drawer open or
     close, a lens or range change) - and nothing ever listened. So the panel
     chips froze at panel-open time and a LIVE call was only re-briefed on
     workspace navigations, never when the page's own data changed under it -
     the "primed with what's on screen" chips could show figures the assistant
     then denied knowing. One listener: refresh the display context, and
     during a live call send a silent re-brief when (and only when) the
     situation actually changed. Debounced so a burst of recomputes (a page
     hydrating several reads) briefs once. */
  const envBriefTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const onEnvelope = () => {
      setPlatform(readPlatformContext());
      if (!jarvisNavOn() || !voiceAliveRef.current) return;
      if (envBriefTimerRef.current != null) window.clearTimeout(envBriefTimerRef.current);
      envBriefTimerRef.current = window.setTimeout(() => {
        envBriefTimerRef.current = null;
        if (!voiceAliveRef.current) return;
        try {
          const brief = situationBrief(readPlatformContext());
          if (brief && brief !== lastBriefRef.current) {
            lastBriefRef.current = brief;
            sendContext(`SCREEN UPDATE (hold this context; reply only when the operator speaks): ${brief}`);
          }
        } catch { /* the re-brief is additive; the call carries on */ }
      }, 1200);
    };
    document.addEventListener('ora:context', onEnvelope);
    return () => {
      document.removeEventListener('ora:context', onEnvelope);
      if (envBriefTimerRef.current != null) window.clearTimeout(envBriefTimerRef.current);
    };
  }, [sendContext]);

  /* PUSH layout (Eri 2026-07-05): while the panel is open the page yields
     instead of being overlaid. The class lives on body so every page picks it
     up through one CSS rule; a resize event follows the toggle so charts and
     canvases re-measure their now-narrower containers. */
  useEffect(() => {
    try {
      document.body.classList.toggle('orbi-open', isOpen);
      window.dispatchEvent(new Event('resize'));
    } catch { /* non-browser env */ }
    return () => { try { document.body.classList.remove('orbi-open'); } catch { /* ignore */ } };
  }, [isOpen]);

  const [visualState, setVisualState] = useState<OrbVisualState>('idle');
  useEffect(() => {
    if (!isOpen) {
      setVisualState(connectionState === ConnectionState.ERROR ? 'escalation' : 'idle');
      return;
    }
    if (connectionState === ConnectionState.ERROR) setVisualState('escalation');
    else if (connectionState === ConnectionState.CONNECTING) setVisualState('reconnecting');
    else if (isConnected) setVisualState('active');
    else setVisualState('listening');
  }, [isOpen, isConnected, connectionState]);

  /* The envelope trail (Phase 3): the sections this operator has visited, kept
     across page hops so the orb can reason about where they came from. Recorded
     on mount (this page IS a hop) and read at ask time. */
  const trailRef = useRef<TrailEntry[]>(restored.current?.trail ?? []);
  useEffect(() => {
    const ctx = readPlatformContext();
    trailRef.current = pushTrail(trailRef.current, (ctx.on_screen as any)?.section || ctx.scope.section);
  }, []);

  /* Restore the on-screen context if the panel came back open after a nav. */
  useEffect(() => {
    if (restored.current?.isOpen) setPlatform(readPlatformContext());
  }, []);

  /* Mirror open-state + transcript + trail to sessionStorage on every change,
     and flush synchronously on unload so a navigation fired from a handler
     (typed nav, a navigate button) never races the effect. The subject is
     intentionally NOT persisted (item 3: it primes only from an open panel). */
  const persistRef = useRef<PersistedState>({ isOpen, askLog, prevLog, trail: trailRef.current });
  useEffect(() => {
    persistRef.current = { isOpen, askLog, prevLog, trail: trailRef.current };
    savePersisted(persistRef.current);
  }, [isOpen, askLog, prevLog]);
  useEffect(() => {
    const flush = () => savePersisted({ ...persistRef.current, trail: trailRef.current });
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => { window.removeEventListener('beforeunload', flush); window.removeEventListener('pagehide', flush); };
  }, []);

  /* Fold finalised voice turns into the one persistent chat log so a call and a
     typed exchange read as a single continuous conversation that survives reload.
     useVapi keeps only a rolling last-3 live window; this captures each turn once
     (by its id) before it rolls off, so the restored history is the full thread. */
  const foldedVoiceIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!logs || logs.length === 0) return;
    const fresh = logs.filter(
      (l) => (l.role === 'user' || l.role === 'assistant') && l.text && !foldedVoiceIds.current.has(l.id)
    );
    if (fresh.length === 0) return;
    fresh.forEach((l) => foldedVoiceIds.current.add(l.id));
    setAskLog((prev) => [
      ...prev,
      ...fresh.map((l) => ({
        id: nextId(),
        role: (l.role === 'assistant' ? 'orbi' : 'user') as AskEntry['role'],
        text: l.text,
        voice: true,
      })),
    ]);
  }, [logs]);

  /* Voice persona catalog. Default selection is customer_support (never NOC). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const vid = activeVerticalId();
        /* No tenant, no personas: the dock keeps its neutral built-in voice
           rather than speaking as the pilot on another tenant's URL. */
        if (!sbVoicePersonas || !vid) return;
        const { data, error } = await sbVoicePersonas
          .from('vertical_personas')
          .select('persona_key,title,subtitle,greeting,conversation_starters,persona_type,is_active')
          .eq('vertical_id', vid)
          .eq('persona_type', 'orbi_voice')
          .neq('is_active', false);
        if (cancelled) return;
        if (error) { console.warn('[OrbiDock] persona fetch failed', error.message); return; }
        const rows = (data || []) as VoicePersona[];
        if (rows.length > 0) {
          setPersonas(rows);
          const idx = rows.findIndex((p) => p.persona_key === DEFAULT_VOICE_PERSONA);
          if (idx >= 0) setPersonaIndex(idx);
          else {
            const safe = rows.findIndex((p) => p.persona_key !== 'network_engineer' && p.persona_key !== 'router');
            setPersonaIndex(safe >= 0 ? safe : 0);
          }
        }
      } catch (err) {
        console.warn('[OrbiDock] persona fetch threw', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* SECTION DESK (Phase 3 binding 1): the section->persona binding, read from
     the SAME vertical_personas rows the persona catalog lives on (section
     column) - one registry, never a second. deskLoadedRef stays false on a
     failed read so the dock never claims "no desk" from ignorance; a loaded
     map with no entry for a section IS the honest fail-closed state. */
  const [sectionDesks, setSectionDesks] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const vid = activeVerticalId();
        if (!sbVoicePersonas || !vid) return;   /* deskless, never the pilot's desks */
        const { data, error } = await sbVoicePersonas
          .from('vertical_personas')
          .select('persona_key,title,section')
          .eq('vertical_id', vid)
          .eq('persona_type', 'view')
          .neq('is_active', false)
          .not('section', 'is', null);
        if (cancelled || error) return;
        const map: Record<string, string> = {};
        (data || []).forEach((r: any) => { if (r.section) map[String(r.section)] = String(r.title || r.persona_key); });
        setSectionDesks(map);
      } catch { /* binding unreadable; identity stays deskless, never "no desk" */ }
    })();
    return () => { cancelled = true; };
  }, []);

  /* THE VERTICAL THE DOCK IS STANDING IN (TENANT-1, T8). On a spun-up vertical
     the shell resolves its tenant AFTER this component mounts, so anything read
     once at mount with activeVerticalId() read null and stayed dark, or read the
     first row of whatever the session could see. The scope is announced on
     ora:scope; this state follows it and the reads below re-run on it. */
  const [workspaceVerticalId, setWorkspaceVerticalId] = useState<string | null>(() => activeVerticalId());
  useEffect(() => {
    const sync = () => { try { setWorkspaceVerticalId(activeVerticalId()); } catch { /* keep the last */ } };
    sync();
    document.addEventListener('ora:scope', sync);
    return () => document.removeEventListener('ora:scope', sync);
  }, []);

  /* Resolve the talk-mode voice from persona_voices (rule 42). THE VERTICAL'S
     OWN default voice, not the first active row the session can read (TENANT-1,
     T8): the platform owner reads every vertical, so an unscoped read handed a
     bank's dock whichever tenant's row sorted first, and on an unresolved tenant
     it handed it nothing and the dock was silent. The vertical's personas are
     read first and the voice is one of theirs. useVapi falls back to the
     consolidated voice if this read is unavailable, so the line is never the
     old static voiceId. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!sbVoicePersonas || !workspaceVerticalId) return;
        const { data: personas, error: pErr } = await sbVoicePersonas
          .from('vertical_personas')
          .select('id,persona_key,sort_order')
          .eq('vertical_id', workspaceVerticalId)
          .eq('persona_type', 'orbi_voice')
          .neq('is_active', false)
          .order('sort_order', { ascending: true });
        if (cancelled || pErr) return;
        const ids = (personas || []).map((p: any) => String(p.id)).filter(Boolean);
        if (!ids.length) return;
        /* The default persona is the first by sort order; its default voice row wins,
           then any active voice of the vertical's personas. */
        const { data, error } = await sbVoicePersonas
          .from('persona_voices')
          .select('persona_id,voice_id,voice_model,voice_provider,is_default,is_active,settings')
          .in('persona_id', ids)
          .eq('is_active', true)
          .order('is_default', { ascending: false });
        if (cancelled || error) return;
        const rows = (data || []) as any[];
        const pick = rows.find((r) => String(r.persona_id) === ids[0] && r.is_default) || rows.find((r) => String(r.persona_id) === ids[0]) || rows[0];
        const cfg = toVoiceConfig(pick);
        if (cfg) setVoiceConfig(cfg);
      } catch (err) {
        console.warn('[OrbiDock] persona_voices resolve failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceVerticalId]);

  /* THE WORKSPACE'S OWN PAGES AND SKILLS (TENANT-1, T8). The "Show me" list was
     five ISP starters typed into this file, so a bank was offered Network,
     Billing, Insights and "Billing suspension". It is now built from what the
     vertical actually has: the nav the shell resolved (vertical_pages through
     ORA.config.sections, announced on ora:sections) and the vertical's enabled
     skills, and from nothing else. */
  const [workspaceSections, setWorkspaceSections] = useState<Array<{ key: string; label: string; operatorOnly?: boolean }>>([]);
  useEffect(() => {
    const read = () => {
      try {
        const ora: any = (workspaceWindow() as any)?.ORA || (window as any).ORA;
        const secs = (ora?.config?.sections || []) as any[];
        const clientView = ora?.scope?.altitude === 'client' || ora?.auth?.role === 'client';
        setWorkspaceSections(secs
          .filter((x) => x && x.key && x.label && !(clientView && x.operatorOnly))
          .map((x) => ({ key: String(x.key), label: String(x.label), operatorOnly: !!x.operatorOnly })));
      } catch { setWorkspaceSections([]); }
    };
    read();
    document.addEventListener('ora:sections', read);
    document.addEventListener('ora:scope', read);
    return () => { document.removeEventListener('ora:sections', read); document.removeEventListener('ora:scope', read); };
  }, []);
  /* THE SKILLS READ WENT WITH THE STARTERS IT FED (D7a, 2026-09-18). This
     effect existed only to turn each enabled skill into a "what has the X skill
     handled recently" button in the Show me list. That list is the server's
     answer now, so the read has no consumer, and an unused read of a tenant
     table is a surface with no reason to be open. */

  const customerRef: CustomerRef | null = useMemo(
    () => (customer ? { account_id: customer.id, account_number: customer.account_number, phone: customer.phone, email: customer.email } : null),
    [customer]
  );

  /* SECTION WATCH (Phase 3 binding 2): on opening a section, the assistant has
     ALREADY read that section's work-stream (the bell's own feed) and leads
     with the one thing that matters, plus an action card through the SAME
     registry every block uses. One watch per section per short window (a page
     hop replays the persisted thread, so re-appending each open would stack
     copies); a failed feed read simply opens the panel without a watch. The
     latest line is kept for voice call starts. */
  const watchInflightRef = useRef(false);
  const lastWatchLineRef = useRef<string | null>(null);
  const runSectionWatch = useCallback(async () => {
    const ctx = readPlatformContext();
    const section = ctx.scope.section || null;
    if (!section || watchInflightRef.current) return;
    const stampKey = `orbi_watch_v1:${ctx.scope.client_slug || 'vertical'}:${section}`;
    try {
      const at = Number(sessionStorage.getItem(stampKey) || 0);
      if (at && Date.now() - at < 10 * 60 * 1000) return;
    } catch { /* storage blocked: watch each open */ }
    watchInflightRef.current = true;
    try {
      const ws = await fetchWorkStream(ctx);
      if (!ws) return;
      const watch = composeSectionWatch(section, ws as any, ctx);
      if (!watch || !watch.line) return;
      lastWatchLineRef.current = watch.line;
      const ui = generativeUiOn() ? validateUiBlocks(watch.blocks) : [];
      try { sessionStorage.setItem(stampKey, String(Date.now())); } catch { /* best effort */ }
      setAskLog((prev) => dedupeThread([...prev, { id: nextId(), role: 'orbi', text: watch.line, ...(ui.length ? { ui } : {}) }]));
    } finally {
      watchInflightRef.current = false;
    }
  }, []);

  const openPanel = useCallback(() => {
    setPlatform(readPlatformContext());
    setIsOpen(true);
    void runSectionWatch();
  }, [runSectionWatch]);

  const stopSession = useCallback(async () => {
    try { await stopCall(); } finally { setIsOpen(false); setCards([]); setVoiceBlocks([]); }
  }, [stopCall]);

  /* Opt-in voice. Runs inside the Talk-button gesture so the mic unlocks.
     The dock is the STAFF console, so every dock-initiated call is the OPERATOR
     copilot (Eri review item 1): it talks to staff, never runs a customer or
     unknown-caller script, and never asks the operator to identify themselves.
     When a subscriber is explicitly open, their name + account number fold into
     the operator context as the SUBJECT so the copilot can discuss them with
     the operator - it does not roleplay a call to them. (Customer-mode voice
     assembly remains untouched for genuinely customer-facing surfaces.) */
  const startVoice = useCallback(async () => {
    if (sessionInflightRef.current) return;
    sessionInflightRef.current = true;
    try {
      const seen = (() => { try { return localStorage.getItem(MIC_PREFLIGHT_KEY) === '1'; } catch { return false; } })();
      if (!seen) {
        setShowMicHint(true);
        try { localStorage.setItem(MIC_PREFLIGHT_KEY, '1'); } catch {}
        setTimeout(() => setShowMicHint(false), 4000);
      }
      const ctxNow = readPlatformContext();
      /* ONE SUBJECT (2026-08-26): same law as onAsk - the page's open panel
         (envelope record_in_view) supersedes a stale primed subject at call
         start, so the voice agent opens on the account the operator is
         actually looking at. (ctxNow is read live, not from the panel-open
         snapshot, for the same reason.) */
      const recNow: any = (ctxNow.on_screen as any)?.record_in_view;
      const panelSubject = recNow && recNow.kind === 'subscriber' && recNow.account_number
        ? { name: (recNow.name as string) || undefined, account_number: String(recNow.account_number) }
        : null;
      const subjectNow = panelSubject && panelSubject.account_number !== customer?.account_number
        ? panelSubject
        : (customer?.name || customer?.account_number ? { name: customer?.name, account_number: customer?.account_number } : null);
      if (panelSubject && panelSubject.account_number !== customer?.account_number) {
        setCustomer({ name: panelSubject.name, account_number: panelSubject.account_number });
      }
      const opCtx = buildOperatorVoiceContext(ctxNow, {
        subject: subjectNow,
        recent_sections: trailSummary(trailRef.current, (ctxNow.on_screen as any)?.section || ctxNow.scope.section) || null,
        operator_name: readGreetingName(),
        /* ONE PATH (audit A1): typed and spoken Jarvis share the conversation.
           The voice call starts with the typed panel's recent turns folded in,
           so what the operator just resolved by typing is known when they talk. */
        recent_thread: askLog.slice(-8)
          .map((e) => `${e.role === 'user' ? 'operator' : 'assistant'}: ${String(e.text || '').replace(/\s+/g, ' ').slice(0, 240)}`)
          .join('\n') || null,
      }) as any;
      // Full subject record at call start (round 2 item 7): trust confidence,
      // billing, connection - live rows, best-effort, never invented.
      if (opCtx._operator && subjectNow?.account_number) {
        try { opCtx._operator.subject_detail = await fetchSubjectDetail(ctxNow, subjectNow.account_number); }
        catch { /* detail is additive; the call still starts with name+number */ }
      }
      // SECTION WATCH (Phase 3 binding 2): the latest watch line rides the
      // call start so a voice session opens already knowing the section's
      // one thing that matters (same feed, same words as the typed panel).
      if (opCtx._operator && lastWatchLineRef.current) {
        opCtx._operator.section_watch = lastWatchLineRef.current;
      }
      // SECTION DESK (Phase 3 binding 1): tell the voice agent who owns this
      // view, or - only when the catalogue actually loaded - that nobody does
      // yet, so it says so instead of inventing an owner. An unloaded
      // catalogue passes nothing (never a claimed absence).
      if (opCtx._operator && sectionDesks) {
        const sec = ctxNow.scope.section || null;
        if (sec) {
          const desk = sectionDesks[sec] || null;
          if (desk) opCtx._operator.section_desk = desk;
          else opCtx._operator.section_desk_missing = true;
        }
      }
      // TTS/ASR FIX (2026-08-26): prime the transcriber with the proper nouns
      // already on screen - the client lens, the known client names, active
      // outage area labels, the open subscriber - so a spoken "Wichita Falls"
      // is heard as the area the operator is looking at, not a soundalike.
      // Raw candidates only; useVapi normalises and caps via buildDockKeyterms.
      if (opCtx._operator) {
        try {
          const evs = readOutageEvents(ctxNow);
          opCtx._operator.keyterms = [
            ctxNow.scope.client_name,
            ...ctxNow.clients.map((c) => c.name),
            ...evs.map((e) => e.area_label || e.area_key),
            recNow?.name,
            subjectNow?.name,
          ];
        } catch { /* keyterms are additive; the call still starts */ }
      }
      opCtx.persona_voice = voiceConfig;
      // Phase 4: expose show_blocks to the operator voice agent only when the
      // generative UI flag is on at call start (same kill switch as typed ask).
      if (opCtx._operator) opCtx._operator.generative_ui = generativeUiOn();
      // JARVIS NAV: expose the one-brain jarvis_navigate tool (and its prompt
      // law) only when the flag is on at call start - same kill switch shape.
      if (opCtx._operator) opCtx._operator.jarvis_nav = jarvisNavOn();
      // JARVIS SITUATIONAL AWARENESS: fold the LIVE situation (page identity,
      // client lens, salient on-screen data) into the call-start context, so
      // "what's happening, a lot is down" is answerable before a word - and
      // the tenant's capability catalogue + its tool names, so the agent can
      // offer capabilities BY NAME. Situation rides ORBI_JARVIS_NAV; the
      // capability tools additionally ride ORBI_GENERATIVE_UI (they paint
      // preview blocks). All additive: any failure starts the call without.
      if (opCtx._operator && jarvisNavOn()) {
        try {
          opCtx._operator.situation = situationBrief(ctxNow);
          if (generativeUiOn()) {
            const caps = await resolvedCapabilities(ctxNow);
            if (caps.length) {
              opCtx._operator.capability_lines = capabilityLines(caps, false);
              opCtx._operator.capability_tools = Array.from(new Set(caps.flatMap((c) => c.tool_names)));
            }
          }
        } catch { /* additive priming; the call still starts */ }
      }
      // JARVIS RESOLVE: the situation primed by the network Resolve control
      // rides a DISTINCT stamp (resolve_situation) so a pill-primed session
      // opens on the outage without hijacking ordinary nav-primed calls, and
      // the mass-resolve tools additionally arm when the page carries the
      // resolve bridge and an active outage event exists - the capability
      // catalogue (config) and this page-truth path dedup by tool name.
      if (opCtx._operator) {
        const sit = situationRef.current;
        if (sit && jarvisNavOn() && generativeUiOn()) {
          opCtx._operator.resolve_situation = describeSituation(sit);
          opCtx._operator.mass_resolve = !!((window as any).OraJarvisResolve && sit.outage_event);
        }
      }
      await startCall(opCtx as unknown as CustomerContext, 'operator');
    } catch (err) {
      console.error('[OrbiDock] start voice failed', err);
    } finally {
      sessionInflightRef.current = false;
    }
  }, [customer, startCall, voiceConfig, resolvedCapabilities, askLog, sectionDesks]);

  const appendOrbi = useCallback((text: string, citations?: AskCitation[], nav?: AskEntry['nav']) => {
    setAskLog((prev) => {
      // Identical consecutive orbi line (the repeated error card) says nothing new.
      const last = prev[prev.length - 1];
      if (last && last.role === 'orbi' && last.text === text && !last.callOptions) return prev;
      return [...prev, { id: nextId(), role: 'orbi', text, citations, nav }];
    });
  }, []);

  /* JARVIS J1: navigate to a config-resolved route. Routes come from the
     jarvis_intents rows via the classifier, never from model prose; still,
     only same-origin paths are honoured (no protocol-relative escapes).
     JARVIS NAV: while a voice call is LIVE (and the flag is on) the
     navigation happens IN PLACE through the workspace layer, so the call
     never drops; every other case stays a normal document load.
     SPEECH SYNC: the return value is the ACTUAL outcome, not an assumption -
     callers that talk about the navigation (the voice tool, panel taps
     mid-call) must speak from it. A voice-originated navigation never falls
     back to a document load mid-call (that load would destroy the live call
     the agent is speaking on); it defers to call end and says so. */
  const deferredVoiceNavRef = useRef<string | null>(null);
  const navigateRoute = useCallback((route: string, opts?: { voice?: boolean }): NavOutcome => {
    if (!route.startsWith('/') || route.startsWith('//')) {
      return { moved: false, mode: 'refused', reason: 'the target is not a screen on this dashboard, so the request was blocked' };
    }
    if (jarvisNavOn() && voiceAliveRef.current) {
      if (workspaceNavigate(route)) return { moved: true, mode: 'in-place' };
      if (opts?.voice) {
        deferredVoiceNavRef.current = route;
        /* REFUSE-THEN-NAVIGATE FIX (2026-08-26): a deferred navigation is a
           SCHEDULED move, not a failure - this exact reason string reaches the
           agent's speech, and the old failure-shaped phrasing ("could not
           open") is what the operator heard as "the system isn't allowing me"
           right before the deferred nav then fired at call end anyway. */
        return { moved: false, mode: 'deferred', reason: 'the screen is queued and will open by itself the moment this call wraps up' };
      }
    }
    window.location.href = route;
    return { moved: true, mode: 'full-load' };
  }, []);

  const navigate = useCallback((slug: string, section: string, opts?: { voice?: boolean }): NavOutcome => {
    try {
      const ORA: any = (window as any).ORA;
      const href = ORA?.href ? ORA.href(section, slug) : (slug ? `/c/${slug}/${section}` : `/${section}`);
      return navigateRoute(href, opts);
    } catch (err) {
      console.warn('[OrbiDock] navigate failed', err);
      return { moved: false, mode: 'refused', reason: 'the route for that screen could not be built' };
    }
  }, [navigateRoute]);

  /* ONE-BRAIN impl (JARVIS NAV): spoken command -> the deterministic server
     classifier (jarvis_resolve_intent + J2/J3/J4 executors) -> the SAME
     renderers as the typed path. A navigation match executes through
     navigateRoute, which mid-call goes IN PLACE via the workspace layer, so
     "take me to customer 55102" happens while the operator keeps talking.
     The exchange folds into the one chat log; blocks validate through the
     same registry. A classifier miss returns matched:false so the agent
     answers in speech from its own grounded context - never a dead end. */
  const jarvisVoiceCommand = useCallback(async (utterance: string): Promise<Record<string, unknown>> => {
    if (!jarvisNavOn()) {
      return {
        matched: false, navigated: false, refused: true,
        reason: 'navigation is not enabled in this console',
        note: 'If the operator asked to go somewhere, say honestly that navigation is not enabled in this console session; then answer their question in speech.',
      };
    }
    const ctx = readPlatformContext();
    // SPEECH SYNC: the classify/ask round trip is the ONLY step that can fail
    // before a route even exists, so it is the ONLY step wrapped in a catch
    // that reports navigated:false. Once navigateRoute has actually moved the
    // screen, that outcome is locked in and returned immediately - nothing
    // downstream (card/UI trimmings) can downgrade a real navigation into a
    // false "did not open", and nothing here ever speaks a raw error: a
    // failure is logged to the console only (DISCLOSURE GUARDRAIL), never
    // interpolated into model-facing content.
    let res: Awaited<ReturnType<typeof askOrbi>>;
    try {
      res = await askOrbi(utterance, ctx, customerRef, {
        uiBlocks: generativeUiOn(),
        jarvisNav: true,
        classifyOnly: true,
      });
    } catch (err) {
      console.warn('[OrbiDock] jarvisVoiceCommand classify failed:', err);
      return { matched: false, navigated: false, note: 'The navigation service did not respond; say so plainly with no technical detail if they asked to go somewhere, and answer in speech.' };
    }
    if (!res.ok) {
      /* REFUSE-THEN-NAVIGATE FIX (2026-08-26): an auth state is not a service
         outage. Flattening "your session has expired, sign in again" into a
         generic "navigation service is unavailable" is what the operator heard
         as "the system isn't allowing me" - right before the auth-free typed
         fast path navigated anyway. The honest reason plus the fix travels to
         speech verbatim; only a genuinely unknown failure keeps the generic
         line. */
      if (res.auth_failure && res.answer) {
        return { matched: false, navigated: false, refused: true, reason: res.answer, note: `${res.answer} Tell the operator this exactly - it is a sign-in state, not a navigation fault; never say the system is not allowing you.` };
      }
      return { matched: false, navigated: false, note: 'The navigation service is unavailable; say so if they asked to go somewhere, and answer in speech.' };
    }
    if (res.used_model === 'jarvis_classify_only') {
      /* REFUSE-THEN-NAVIGATE FIX (2026-08-26): a server classifier miss on a
         plainly-phrased section command used to dead-end here as matched:false,
         inviting the agent to improvise a refusal - after which one of the
         OTHER navigators (a typed retry, a card tap, the deferred executor)
         happily moved the screen. Close the gap with the SAME deterministic
         client-side detector the typed path trusts: an allowlisted section
         command still navigates, in place, on the first ask. Anything it
         cannot match remains an honest matched:false. */
      const navCmd = detectNavCommand(utterance, ctx.clients);
      if (navCmd) {
        const nav = navigate(navCmd.client_slug || ctx.scope.client_slug || '', navCmd.section, { voice: true });
        if (nav.moved) {
          appendOrbi(`Opening ${navCmd.section}${navCmd.client_name ? ` for ${navCmd.client_name}` : ''}.`);
          return {
            matched: true, navigated: true, route: navCmd.section,
            note: 'The screen IS opening now, in place beside this call; the call stays live. Confirm the move briefly, speaking it as something you just did.',
          };
        }
        return {
          matched: true, navigated: false,
          [nav.mode === 'deferred' ? 'deferred' : 'refused']: true,
          reason: nav.reason,
          note: nav.mode === 'deferred'
            ? `The screen is queued to open by itself the moment this call ends. Say that as a scheduled action ("it will be up right after this call") - never as a failure, and never that something is not allowing you.`
            : `The screen did NOT open. Tell the operator honestly: ${nav.reason}. Do not claim it opened.`,
        };
      }
      return { matched: false, navigated: false, note: 'No deterministic match; answer from your own context and tools.' };
    }
    const route = res.navigate?.route || null;
    // Best-effort UI trimmings (chat log, generative blocks, subject card).
    // A failure here is cosmetic; it must never touch the nav outcome or leak
    // technical detail into speech.
    const paintTrimmings = () => {
      try {
        if (res.answer) appendOrbi(res.answer, res.citations, route ? { route } : undefined);
        const ui = validateUiBlocks(res.ui);
        if (ui.length) setVoiceBlocks(ui.filter((b) => b.type !== 'report_link').slice(0, 2));
        if (res.card) pushCard(res.card.type || 'subscriber', res.card.payload || {});
      } catch (err) {
        console.warn('[OrbiDock] jarvisVoiceCommand UI trimmings failed:', err);
      }
    };
    if (route) {
      // SPEECH SYNC: navigated reports what navigateRoute ACTUALLY did, so
      // the agent's words can never disagree with the screen. A classifier
      // route the guard refuses, or a mid-call deferral, comes back as
      // navigated:false with the honest reason to speak.
      const nav = navigateRoute(route, { voice: true });
      if (nav.moved) {
        paintTrimmings();
        return {
          matched: true, navigated: true, route, answer: res.answer,
          note: 'The screen IS opening now, in place beside this call; the call stays live. Confirm the move briefly, speaking it as something you just did.',
        };
      }
      paintTrimmings();
      return {
        matched: true, navigated: false, route, answer: res.answer,
        [nav.mode === 'deferred' ? 'deferred' : 'refused']: true,
        reason: nav.reason,
        /* REFUSE-THEN-NAVIGATE FIX (2026-08-26): a deferral is a scheduled
           move that WILL happen at call end - the agent must speak it that
           way, never as a failure it then contradicts by navigating. */
        note: nav.mode === 'deferred'
          ? `The screen is queued to open by itself the moment this call ends. Say that as a scheduled action ("it will be up right after this call") - never as a failure, and never that something is not allowing you.`
          : `The screen did NOT open. Tell the operator honestly: ${nav.reason}. Do not claim it opened.`,
      };
    }
    paintTrimmings();
    return {
      matched: true, navigated: false, answer: res.answer,
      note: res.answer
        ? 'This grounded answer is already on the panel; speak it (or its gist) to the operator.'
        : 'Matched but nothing to show; say so plainly.',
    };
  }, [customerRef, appendOrbi, navigateRoute, navigate, pushCard]);
  useEffect(() => { jarvisCommandImplRef.current = jarvisVoiceCommand; }, [jarvisVoiceCommand]);

  /* Shared dialer used by the inline button, the subscriber flyout, and the chat
     "call X" intent. Confirms once, then routes through requestOrbiCall, which the
     server gates against the outbound test allowlist. */
  const placeCallTo = useCallback(async (target: { name?: string | null; account_number?: string | null; phone?: string | null; account_id?: string }, reason?: string) => {
    const ctx = platform || readPlatformContext();
    const who = target.name || target.account_number || 'this subscriber';
    const why = reason ? ` to ${reason}` : '';
    if (!window.confirm(`Call ${who}${why}? The call connects only if their number is on the outbound test allowlist; any other number is held as a dry run.`)) return;
    setCallPending(true);
    try {
      const ref: CustomerRef = { account_id: target.account_id, account_number: target.account_number || undefined, phone: target.phone || undefined };
      const r = await requestOrbiCall(ctx, ref, { dry_run: false, reason });
      appendOrbi(r.message);
    } finally {
      setCallPending(false);
    }
  }, [platform, appendOrbi]);

  const onAsk = useCallback(async (text: string) => {
    /* CONTEXT BRIDGE (2026-08-14): the envelope is read LIVE on every ask, never
       from the panel-open snapshot. The page republishes on every data load,
       drawer open/close and lens change, so a snapshot taken at panel-open time
       goes stale the moment the operator opens a drawer or expands an incident -
       which is exactly the "it is not seeing things I have opened" failure. The
       cached `platform` state remains display-only (chips, identity). */
    const ctx = readPlatformContext();
    setPlatform(ctx);
    /* ONE SUBJECT (2026-08-26): the page's open panel (envelope record_in_view)
       is the source of truth for who is on screen; the server now resolves the
       subject envelope-first, and this mirror keeps the dock's own subject
       display and voice priming in step with it, so the page panel and the
       assistant panel can never name two different accounts. A missing
       envelope record leaves the primed subject alone (a subject primed by
       lookup or a typed resolution is real conversational state with no panel
       behind it). */
    const recInView: any = (ctx.on_screen as any)?.record_in_view;
    if (recInView && recInView.kind === 'subscriber' && recInView.account_number
        && String(recInView.account_number) !== customer?.account_number) {
      setCustomer({ name: recInView.name || undefined, account_number: String(recInView.account_number) });
    }
    setAskLog((prev) => [...prev, { id: nextId(), role: 'user', text }]);
    // Explicit typed navigation ("take me to billing", "go to customers for
    // conduct connect") executes directly - the phrasing is the confirmation.
    // Allowlisted sections only; anything else falls through to the ask.
    // ONE-BRAIN (JARVIS NAV): with the flag on, bare section commands defer to
    // the SAME server classifier the voice path uses (the jarvis_intents
    // config now carries the take-me/go-to forms), so typed and spoken route
    // through one brain. The client-side fast path is kept ONLY for commands
    // that name another client lens ("billing for conduct connect") - lens
    // switching is a host/viewport concern the subject-scoped classifier
    // deliberately does not own - and as the whole path when the flag is off.
    const navCmd = detectNavCommand(text, ctx.clients);
    if (navCmd && (!jarvisNavOn() || navCmd.client_slug)) {
      const where = `${navCmd.section}${navCmd.client_name ? ` for ${navCmd.client_name}` : ''}`;
      setAskLog((prev) => [...prev, { id: nextId(), role: 'orbi', text: `Opening ${where}.` }]);
      navigate(navCmd.client_slug || ctx.scope.client_slug || '', navCmd.section);
      return;
    }
    // THE PAGE'S OWN READ INTENTS FIRST (polish wave 12). The host page's registry
    // answers "what am I looking at", "which figures could not be read" and "who is
    // open" from what it painted, in the browser; nothing leaves the page. The dock's
    // readback below stays as the fallback for a page with no registry.
    const pageAnswer = pageIntentAnswer(text);
    if (pageAnswer) {
      appendOrbi(pageAnswer);
      return;
    }
    // "What am I looking at" answered directly from the on-screen envelope (rule 32),
    // no backend round trip and nothing invented. Kept ahead of the ask backend so
    // the screen readback is instant and always grounded in the current page.
    if (isWhatOnScreenQuery(text)) {
      appendOrbi(answerWhatOnScreen(ctx));
      return;
    }
    // "Call <name>" or "show me <name>": resolve the subscriber by name and surface
    // them as generative cards, each with its own Call button. Single match -> one
    // card; several -> a pick list. Reuses the proven allowlist-gated dialer, and
    // never auto-dials: the operator confirms by tapping Call on the right person.
    // The card renders in the orb-owned layer, which now sits above the customer
    // slide-out (Phase 2 z-index fix), so voice and card stay usable together.
    const callIntent = detectCallIntent(text);
    const showIntent = callIntent ? null : detectShowIntent(text);
    const subjectIntent = callIntent || showIntent;
    if (subjectIntent) {
      const forCall = !!callIntent;
      const reason = callIntent?.reason;
      setAskPending(true);
      try {
        const cands = await searchSubscribersByName(ctx, subjectIntent.name);
        if (cands.length === 0) {
          appendOrbi(`I could not find a subscriber named "${subjectIntent.name}". Check the spelling, or try the account number.`);
        } else {
          const opts = cands.map((c) => ({ name: c.name, account_number: c.account_number, phone: c.phone, status: c.status, building_slug: c.building_slug, is_test: c.is_test, account_id: c.account_id, reason }));
          const lead = cands.length === 1
            ? (forCall
                ? `Here is ${cands[0].name}${reason ? `, to ${reason}` : ''}. Tap Call when you are ready.`
                : `Here is ${cands[0].name}. Tap Call when you want me to reach them.`)
            : (forCall
                ? `I found ${cands.length} subscribers matching "${subjectIntent.name}"${reason ? ` (to ${reason})` : ''}. Who do you want to call?`
                : `I found ${cands.length} subscribers matching "${subjectIntent.name}". Which one?`);
          setAskLog((prev) => [...withoutPriorCallCard(prev, callCardKey(opts)), { id: nextId(), role: 'orbi', text: lead, callOptions: opts }]);
          /* ONE PATH (audit A1): a UNIQUE typed resolution primes the SUBJECT the
             same way the voice lookup tool does (only the voice path used to call
             setCustomer, which is why spoken Jarvis was clueless about the card
             typed Jarvis had just rendered). Ambiguous matches never prime —
             the operator picks from the card first (never auto-pick). */
          if (cands.length === 1) {
            const c0 = cands[0];
            setCustomer({ id: c0.account_id, name: c0.name, account_number: c0.account_number || undefined, phone: c0.phone || undefined });
          }
        }
      } catch (err) {
        // The auth message is already the operator-facing sentence; wrapping it
        // in "I could not search subscribers (...)" would bury the instruction.
        const authText = operatorAuthFailureText(err);
        appendOrbi(authText ?? `I could not search subscribers (${(err as Error).message}).`);
      } finally {
        setAskPending(false);
      }
      return;
    }
    setAskPending(true);
    try {
      const wantUi = generativeUiOn();
      // Fold the envelope trail into the page context (Phase 3): the ask
      // backend's PAGE CONTEXT block renders recent_sections verbatim, so the
      // orb knows the path that led here with no server change. The published
      // envelope itself is never mutated.
      const recent = trailSummary(trailRef.current, (ctx.on_screen as any)?.section || ctx.scope.section);
      const askCtx = recent ? { ...ctx, on_screen: { ...(ctx.on_screen as any), recent_sections: recent } } : ctx;
      /* ONE PATH (audit A1): the request carries this conversation's recent turns. */
      const recentTurns = askLog.slice(-8).map((e) => ({ role: e.role === 'user' ? 'operator' : 'assistant', text: String(e.text || '').slice(0, 300) }));
      const res = await askOrbi(text, askCtx, customerRef, { uiBlocks: wantUi, jarvisNav: jarvisNavOn(), recentTurns });
      const nav = res.navigate
        ? (res.navigate.route
            ? { route: res.navigate.route, auto: res.navigate.auto !== false }
            : { client_slug: res.navigate.client_slug || '', section: res.navigate.section || '' })
        : undefined;
      // Registry-validate the generative blocks CLIENT-side too (defence in
      // depth); anything unknown or out of contract is dropped before render.
      const ui = wantUi ? validateUiBlocks(res.ui) : [];
      // Show the subject inline as a card (replaces the old "open the user" nudge);
      // a follow-up with no subject clears the stale card. When a generative
      // subscriber_card block arrived, it supersedes the legacy card so the same
      // subscriber never paints twice.
      const hasUiSubscriber = ui.some((b) => b.type === 'subscriber_card');
      setCards(res.card && !hasUiSubscriber ? [{ id: nextId(), type: res.card.type || 'subscriber', payload: res.card.payload || {} }] : []);
      /* ONE PATH (audit A1): a typed ask that resolved a subject primes the SAME
         subject store the voice path reads — spoken Jarvis on this screen now
         knows exactly who typed Jarvis just rendered a card for. */
      if (res.subject && (res.subject.account_number || res.subject.name)) {
        setCustomer({ id: res.subject.account_id || undefined, name: res.subject.name || undefined, account_number: res.subject.account_number || undefined });
      }
      // dedupeThread: a repeat ask about the same subscriber REPLACES the earlier
      // subscriber_card instead of stacking a twin (the A5 gap — this append was bare).
      setAskLog((prev) => dedupeThread([...prev, { id: nextId(), role: 'orbi', text: res.answer, citations: res.citations, nav, ...(ui.length ? { ui } : {}) }]));
      // JARVIS J1: a classifier navigation match executes like the typed nav
      // command path above (answer first, then the surface opens); the chip
      // stays in the log as the history. Same-origin only via navigateRoute.
      /* A NAVIGATION THAT IS THE ANSWER MOVES THE SCREEN; ONE THAT IS AN OFFER
         DOES NOT (D7a, 2026-09-18). "Show me the appointment" asked to be taken
         there, so being taken there is the reply. "Which enquiries went cold"
         asked a question this page cannot answer, and relocating the operator in
         response is the assistant deciding where they work: the reply names the
         page and the chip waits for a tap. */
      if (nav && nav.route && (nav as { auto?: boolean }).auto !== false) {
        window.setTimeout(() => navigateRoute(nav.route!), 900);
      }
    } catch (err) {
      appendOrbi(`I could not answer that (${(err as Error).message}).`);
    } finally {
      setAskPending(false);
    }
  }, [customerRef, appendOrbi, placeCallTo, askLog]);

  /* Dispatch a validated generative-UI action. Every path is an existing, gated
     one (rule 51): navigate confirms on tap then uses the host router (a typed
     "take me there" skips the confirm - the phrasing is the consent), call runs
     the confirm-on-tap dialer against the server allowlist, ask feeds the
     follow-up prompt back in. */
  const onUiAction = useCallback((a: UiAction) => {
    if (a.action === 'navigate' && a.params.section) {
      const where = `${a.params.section}${a.params.client_slug ? ` for ${a.params.client_slug}` : ''}`;
      if (!window.confirm(`Open ${where}? This leaves the current page.`)) return;
      navigate(a.params.client_slug || '', a.params.section);
      return;
    }
    if (a.action === 'call' && a.params.account_number) {
      void placeCallTo({ name: a.params.name || null, account_number: a.params.account_number }, a.params.reason);
      return;
    }
    if (a.action === 'ask' && a.params.prompt) {
      void onAsk(a.params.prompt);
      return;
    }
    // Phase 6: a validated report download (the registry only passes signed
    // URLs on our own storage origin). Opens in a new tab; nothing executes.
    if (a.action === 'download' && a.params.url) {
      window.open(a.params.url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Round 3 item 4: the inline resolve-preview's "Confirm & fire" button
    // drives the SAME onResolveFire path the voice tool uses - the native
    // window.confirm() inside it is still the one fire-confirm gate, whether
    // the operator got here by voice or by clicking the panel.
    if (a.action === 'resolve_fire') {
      void onResolveFire();
    }
  }, [navigate, placeCallTo, onAsk, onResolveFire]);

  /* runAction, runNetwork, runBilling and runDiscovery were the three hardcoded
     ISP starters this file typed in, and the helper that ran them. They are gone
     with the list that offered them (D7a): a workspace's starters are now the
     server's answer, in the workspace's own words, and an ask-kind starter runs
     through onAsk like any other question. */

  /* Inline "Call [subscriber]" (item 7). TEST-SAFE by the SERVER: orbi-operator-call
     only places a real call when the dialed number is on the ORBI_OUTBOUND_ALLOWLIST
     env; every other number fails safe to a dry run server-side. So this requests a
     real call and the panel reports back what actually happened (dialed vs held). */
  const onCallSubject = useCallback(async () => {
    if (!customerRef?.phone && !customerRef?.account_number) {
      appendOrbi('Prime a subscriber first, then I can place an outbound call.');
      return;
    }
    await placeCallTo({ name: customer?.name, account_number: customerRef.account_number, phone: customerRef.phone, account_id: customerRef.account_id });
  }, [customerRef, customer, placeCallTo, appendOrbi]);

  /* A shortcut resolves to a route INSIDE the workspace the operator is standing
     in (TENANT-1, T8): ORA.href() with no slug applies the shell's own rule, so a
     sole-client vertical stays at /v/<slug>/<section> rather than walking to a
     /c/ address that may not resolve. Never the platform root. */
  const openSection = useCallback((key: string) => {
    try {
      const ora: any = (workspaceWindow() as any)?.ORA || (window as any).ORA;
      const href = ora?.href ? String(ora.href(key)) : `/${key}`;
      if (!href.startsWith('/') || href.startsWith('//')) return;
      navigateRoute(href);
    } catch (err) {
      console.warn('[OrbiDock] openSection failed', err);
    }
  }, [navigateRoute]);

  /* ── STARTERS COME FROM THE SERVER (D7a, 2026-09-18) ────────────────────
     WHAT WAS HERE. A list built in this file from the workspace's nav plus its
     skills: one button per page of the rail, plus three ISP-shaped asks. On
     Eri's healthcare workspace the assistant's whole opening offer was
     therefore the left navigation, restated - which tells an operator nothing
     they cannot already see, and tells a workspace that has not been set up
     nothing about how to set it up.

     WHAT IT IS NOW. orbi-operator-ask answers it, because the two things the
     list depends on both live there: the spin-up completion state (a read of six
     tenant stores behind RLS, which a browser must not reach directly and which
     must not become a second checklist), and the workspace's own words.

     While spin-up is unfinished the list is the next three incomplete steps as
     OFFERS, each one tap to the page that finishes it with the control focused.
     Once it is finished it is the three most useful questions for the page the
     operator is on. It is never the navigation list again.

     FAILS TO NOTHING. No answer, a refusal, an error: the list is empty and the
     panel shows no starters. A typed-in fallback is precisely what this replaced. */
  /* ── THE WORKSPACE'S WORD FOR A RECORD (D7a, 2026-09-18) ────────────────
     ONE OWNER, and it is not this file. lib/vocabulary.js reads
     public.vertical_vocabulary through the page's own scoped client, and
     components/canonical/ora-config.js exposes it as ORA.vocabulary(). The rail,
     every page heading and every empty state already ask it; the assistant panel
     was the last surface still typing "subscriber" into its source.

     Resolves on EVERY path. No ORA, no reader, a refused read: the platform's
     vertical-neutral word stands, which is "Customer" and never "Subscriber" -
     an ISP noun as the platform default is the literal rule 6 exists to remove. */
  const [workspaceWords, setWorkspaceWords] = useState<ChipWords>({});
  useEffect(() => {
    let cancelled = false;
    const read = () => {
      try {
        const ora: any = (workspaceWindow() as any)?.ORA || (window as any).ORA;
        if (!ora || typeof ora.vocabulary !== 'function') return;
        Promise.resolve(ora.vocabulary()).then((v: any) => {
          if (cancelled || !v || typeof v.label !== 'function' || typeof v.has !== 'function') return;
          const out: ChipWords = {};
          /* `has` is the whole point: only a term the WORKSPACE has chosen is
             taken. A term it has not chosen falls to the platform's neutral word
             in the consumer, never to the reader's key-humanising fallback,
             which would print "Property" where the platform says "Site". */
          for (const key of ['subscriber', 'property', 'ticket'] as const) {
            if (!v.has(key)) continue;
            const one = String(v.label(key) || '').trim();
            if (!one) continue;
            const many = String(typeof v.plural === 'function' ? v.plural(key) || '' : '').trim();
            out[key] = { one, many: many || one };
          }
          if (Object.keys(out).length) setWorkspaceWords(out);
        }).catch(() => { /* the neutral words stand */ });
      } catch { /* the neutral words stand */ }
    };
    read();
    document.addEventListener('ora:scope', read);
    document.addEventListener('ora:sections', read);
    return () => { cancelled = true; document.removeEventListener('ora:scope', read); document.removeEventListener('ora:sections', read); };
  }, []);
  const recordWord = useMemo(
    () => workspaceWords.subscriber || { one: 'Customer', many: 'Customers' },
    [workspaceWords]
  );

  const [starters, setStarters] = useState<{ kind: 'setup' | 'ask'; items: StarterOffer[] }>({ kind: 'ask', items: [] });
  const startersKeyRef = useRef<string>('');
  const refreshStarters = useCallback(async () => {
    const ctx = readPlatformContext();
    const key = `${ctx.scope.vertical_id || ''}|${ctx.scope.client_id || ''}|${(ctx.on_screen as any)?.section || ctx.scope.section || ''}`;
    if (!ctx.scope.vertical_id || key === startersKeyRef.current) return;
    startersKeyRef.current = key;
    const res = await askStarters(ctx);
    setStarters({ kind: res.kind, items: res.starters });
  }, []);
  useEffect(() => {
    if (!isOpen) return;
    void refreshStarters();
  }, [isOpen, refreshStarters]);
  useEffect(() => {
    const on = () => { startersKeyRef.current = ''; void refreshStarters(); };
    document.addEventListener('ora:scope', on);
    document.addEventListener('ora:sections', on);
    return () => { document.removeEventListener('ora:scope', on); document.removeEventListener('ora:sections', on); };
  }, [refreshStarters]);

  /* ── A DROPPED FILE IS ANSWERED (T5, 2026-09-18) ────────────────────────
     Eri dropped a CSV on the assistant and nothing happened. The panel now
     intercepts the drop and this says what became of it, in one line, with the
     one tap that reaches the import that CAN take it.

     THE IMPORT IS NOT WIRED FROM HERE, deliberately. Every page's import runs
     through components/canonical/empty-state.js against that page's own
     pipeline, declared per page; an upload path inside the assistant would be a
     second route to the same orchestrator and a second place a failed import
     could be reported from, which is how two accounts of one import start
     disagreeing. The assistant's honest job here is to refuse clearly and point
     once. The page it points at is the workspace's own: the first setup step
     still open if there is one, else the page the operator is on. */
  const onFileDropped = useCallback((file: { name: string; type: string }) => {
    const ctx = readPlatformContext();
    const target = starters.items.find((x) => x.kind === 'setup' && x.route) || null;
    const route = target?.route ? (target.focus ? `${target.route}#${target.focus}` : target.route) : null;
    const isCsv = /\.csv$/i.test(file.name) || /csv/i.test(file.type);
    const what = isCsv ? 'that file' : `${file.name}`;
    const lead = isCsv
      ? `I cannot take ${what} here, and nothing was imported.`
      : `I can only take a CSV, and ${what} is not one. Nothing was imported.`;
    const where = route
      ? ' The import that can take it is one tap away.'
      : ' The import lives on the page the records belong to, under its Upload a file button.';
    openPanel();
    setAskLog((prev) => [...prev, {
      id: nextId(),
      role: 'orbi',
      text: lead + where,
      ...(route ? { nav: { route } } : {}),
    }]);
    void ctx;
  }, [starters, openPanel]);

  const quickActions: QuickAction[] = useMemo(() => {
    return starters.items.map((s) => {
      if (s.kind === 'setup') {
        /* A setup offer is a navigation with a control named on the other end.
           The hash is what components/canonical/empty-state.js and the Settings
           panes already read, so "the right control focused" needs no new
           contract between this dock and a page. */
        const to = s.route ? (s.focus ? `${s.route}#${s.focus}` : s.route) : null;
        return {
          key: s.key,
          label: s.label,
          hint: s.hint,
          kind: 'navigate' as const,
          opensLabel: (s.opens_label || s.section || 'the page').toLowerCase(),
          disabled: !to,
          run: () => { if (to) navigateRoute(to); },
        };
      }
      return {
        key: s.key,
        label: s.label,
        hint: s.hint,
        kind: 'ask' as const,
        voiceLine: s.question || s.label,
        run: () => { void onAsk(s.question || s.label); },
      };
    });
  }, [starters, navigateRoute, onAsk]);

  const handle: OrbiDockHandle = useMemo(
    () => ({
      setCustomer: (c) => { setCustomer(c); setPlatform(readPlatformContext()); },
      setPersona: (key: string) => {
        setPersonas((prev) => {
          const idx = prev.findIndex((p) => p.persona_key === key);
          if (idx >= 0) setPersonaIndex(idx);
          return prev;
        });
      },
      open: () => { openPanel(); },
      close: () => { void stopSession(); },
      callCustomer: (c) => {
        if (c) setCustomer(c);
        openPanel();
        if (c) {
          const opt = { name: c.name || c.account_number || 'Subscriber', account_number: c.account_number ?? null, phone: c.phone ?? null, account_id: c.id };
          // The drawer's Call button is the repeat-prone one: it re-fires on every
          // re-open of the same customer, and the drawer outlives page hops.
          setAskLog((prev) => [...withoutPriorCallCard(prev, callCardKey([opt])), { id: nextId(), role: 'orbi', text: `Here is ${opt.name}. Tap Call when you are ready.`, callOptions: [opt] }]);
        }
      },
      showCard: (cardType, payload) => pushCard(cardType, payload),
      /* JARVIS RESOLVE: the network Resolve control lands here. The panel opens
         with an honest situation briefing; the voice context picks the situation
         up at call start, and a call already LIVE is re-pointed immediately
         (same shape as the mid-call subject sync). */
      openSituation: (sit) => {
        setSituation(sit || null);
        openPanel();
        if (!sit) return;
        const ev = sit.outage_event || null;
        const brief =
          `You pressed Resolve on the network view${sit.client_name ? ` for ${sit.client_name}` : ''}: ` +
          `${typeof sit.down_count === 'number' ? sit.down_count : 'some'} connection(s) down.` +
          (ev
            ? ` Active outage: ${ev.area_label || ev.area_key}, ${ev.down_count ?? '?'}${ev.total_in_area ? ` of ${ev.total_in_area}` : ''} down.` +
              ' Hit Talk and ask me to run the resolve. Before anything fires I will show exactly who gets called and who is excluded, and you confirm by voice or the button on the preview. While TEST MODE is on, a sweep can only ring the staff allowlist.'
            : ' No active outage event, so the batch sweep is not armed here; individual resolves run from the Resolve board.');
        setAskLog((prev) => [...prev, { id: nextId(), role: 'orbi', text: brief }]);
        if (voiceAliveRef.current) {
          try { sendMessage(`SITUATION UPDATE: ${describeSituation(sit)} Follow the outage resolve law: preview first, fire only on the operator's explicit confirmation.`); } catch { /* no live channel */ }
        }
      },
    }),
    [openPanel, stopSession, pushCard, sendMessage]
  );

  useEffect(() => {
    if (registerHandle) registerHandle(handle);
  }, [handle, registerHandle]);

  const onDockClick = useCallback(() => {
    if (isOpen) void stopSession();
    else openPanel();
  }, [isOpen, openPanel, stopSession]);

  /* SECTION DESK (Phase 3): the desk shows in ASK mode too - the context chip
     is the ask panel's identity surface (the hero subtitle is voice-only). */
  const contextLine = useMemo(() => {
    const base = platform ? describeContext(platform) : '';
    const section = platform?.scope.section || null;
    const desk = section && sectionDesks ? sectionDesks[section] : null;
    return desk ? (base ? `${base} · ${desk} desk` : `${desk} desk`) : base;
  }, [platform, sectionDesks]);
  const primedChips = useMemo(() => (platform ? readChips(platform, workspaceWords) : []), [platform, workspaceWords]);
  /* Identity is the active client's Orbi in operator mode, derived from the lens.
     SECTION DESK (Phase 3): when the current section has a bound desk persona,
     the subtitle names it - the operator sees WHO answers for this view. An
     unbound or unloaded catalogue changes nothing (never a claimed absence). */
  const derivedIdentity = useMemo(() => {
    const id = platform ? readIdentity(platform.scope) : PLATFORM_IDENTITY;
    const section = platform?.scope.section || null;
    const desk = section && sectionDesks ? sectionDesks[section] : null;
    return desk ? { ...id, subtitle: `${id.subtitle} · ${desk} desk` } : id;
  }, [platform, sectionDesks]);
  /* Greet ONCE per operator session (Eri round 2 item 4): the first panel
     open says hi; every later open goes straight to "Ask me anything". */
  const greetingName = useMemo(() => {
    if (!isOpen) return null;
    try {
      if (sessionStorage.getItem('orbi_greeted_v1') === '1') return null;
      sessionStorage.setItem('orbi_greeted_v1', '1');
    } catch { /* storage blocked; greeting once is best-effort */ }
    return readGreetingName();
  }, [isOpen]);

  const dockClasses = [
    'orb-shape',
    `orb-${visualState}`,
    isOpen ? 'orb-hidden' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* ONE orb on screen, structurally (Eri round 2 item 1): while the panel
          is open the dock button does not RENDER at all, so no CSS state,
          animation, or stacking quirk can ever paint a second orb beside the
          panel's hero. */}
      {!isOpen && (
        <button
          type="button"
          className={dockClasses}
          aria-label="Open your AI assistant"
          aria-pressed={isOpen}
          title="Your AI assistant. Click to ask."
          onClick={onDockClick}
        >
          <span className="orbi-dock-orb" aria-hidden="true">
            <ChromeOrb state="idle" audioLevel={0} scaleFactor={0.9} />
          </span>
          <span className="orbi-dock-persona-pill" aria-hidden="true">AI</span>
          {showMicHint && (
            <span className="orbi-dock-mic-hint" role="status">Mic access is needed to talk</span>
          )}
        </button>
      )}

      {isOpen && (
        <aside className="orbi-side-panel" role="dialog" aria-label="AI assistant panel">
          <VoiceOverlayPanel
            connectionState={connectionState}
            agentState={agentState}
            audioLevel={audioLevel}
            logs={logs}
            micError={micError}
            sendMessage={sendMessage}
            stopCall={stopCall}
            identity={derivedIdentity}
            customer={customer}
            onClose={() => { void stopSession(); }}
            contextLine={contextLine}
            primedChips={primedChips}
            greetingName={greetingName}
            quickActions={quickActions}
            startersKind={starters.kind}
            recordWord={recordWord}
            onFileDropped={onFileDropped}
            askLog={askLog}
            askPending={askPending}
            onAsk={(t) => void onAsk(t)}
            onCallOption={(c) => void placeCallTo(c, c.reason)}
            onUiAction={onUiAction}
            onStartVoice={() => void startVoice()}
            onNavigate={(n) => (n.route ? navigateRoute(n.route) : navigate(n.client_slug || '', n.section || ''))}
            inPlaceNav={jarvisNavOn()}
            subjectName={customer?.name || (customer?.account_number ? `Account ${customer.account_number}` : undefined)}
            /* Same identity the call cards key on, account_number first (callCardKey, audit A5),
               so the panel can tell when a card already offers this subject and stand its own
               inline affordance down — one primary per intent. */
            subjectKey={customerRef?.account_number || customerRef?.account_id || customerRef?.phone || null}
            onCallSubject={() => void onCallSubject()}
            callPending={callPending}
            cards={cards}
            voiceUi={voiceBlocks}
            onDismissCard={(id) => setCards((prev) => prev.filter((c) => c.id !== id))}
            prevCount={prevLog.length}
            /* dedupeThread: the parked thread often holds the SAME call/subscriber
               cards as the live one — a raw splice resurrected every twin (A5). */
            onViewPrevious={() => { setAskLog((cur) => dedupeThread([...prevLog, ...cur])); setPrevLog([]); }}
          />
        </aside>
      )}
    </>
  );
};

export type { OrbiDockHandle, CustomerPriming, SituationPriming };
export default OrbiDock;
